import React, { useState, useEffect } from 'react';
import { Box, Flex, Text, Button, Spinner, Center, useToast } from '@chakra-ui/react';
import { DragDropContext } from 'react-beautiful-dnd';
import { useQueryClient } from '@tanstack/react-query';
import { useLeadQueries, extractStages } from '../hooks/useLeadQueries';
import { useKanbanTable } from '../hooks/useKanbanTable';
import { useSocketIntegration } from '../hooks/useSocketIntegration';
import TanColumn from './TanColumn';
import TanFilters from './TanFilters';

/**
 * TanStack implementation of the Kanban board
 * 
 * This component uses TanStack Query for data fetching and
 * TanStack Table for organizing and displaying data
 */
const TanKanbanBoard = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  
  // State for filters
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for collapsed columns
  const [collapsedColumns, setCollapsedColumns] = useState({
    'stage_closed_won': true,  // Closed Won collapsed by default
    'stage_closed_lost': true   // Closed Lost collapsed by default
  });
  
  // Use lead queries hook for data fetching
  const {
    leads,
    leadsData,
    isLoading,
    isLoadingAllLeads,
    hasMore,
    loadMoreLeads,
    loadAllLeads,
    updateLead,
    isUpdatingLead,
    error,
    isFetchingNextPage // Add this to track when next page is loading
  } = useLeadQueries(filters);
  
  // Import STAGE_IDS directly from the constants
  const { STAGE_IDS } = require('../../NewKB/utils/NewKBConstants');
  
  // Hardcode the stages for consistency
  const hardcodedStages = [
    { _id: STAGE_IDS.LEAD_IN, name: 'Lead In', color: '#4299E1' },
    { _id: STAGE_IDS.NURTURE, name: 'Nurture', color: '#F6AD55' },
    { _id: STAGE_IDS.PROPOSAL_NEEDED, name: 'Proposal Needed', color: '#68D391' },
    { _id: STAGE_IDS.FIX_MEETING, name: 'Fix Meeting', color: '#9F7AEA' },
    { _id: STAGE_IDS.CLOSED_WON, name: 'Closed Won', color: '#48BB78' },
    { _id: STAGE_IDS.CLOSED_LOST, name: 'Closed Lost', color: '#F56565' },
  ];
  
  // ALWAYS use hardcoded stages for consistency
  console.log('Passing to useKanbanTable:', { leadCount: leads.length, stages: hardcodedStages });
  // Note: We're ignoring any stages that might come from the API
  const { table, stages } = useKanbanTable(leads, hardcodedStages);
  console.log('Stages from useKanbanTable:', stages);
  
  // Setup socket integration with correct queryClient
  const { socket, emitWithSourceId } = useSocketIntegration(queryClient);
  
  // Load all leads when component mounts
  useEffect(() => {
    // Auto-load all leads when component mounts
    console.log('TanKanbanBoard: Initiating lead loading');
    loadAllLeads();
  }, [loadAllLeads]);
  
  // Debug leads when they change
  useEffect(() => {
    console.log(`TanKanbanBoard: Leads updated, now have ${leads.length} leads`);
  }, [leads]);
  
  // Show error toast if there's an error
  useEffect(() => {
    if (error) {
      toast({
        title: 'Error loading leads',
        description: error.message || 'There was an error loading the leads. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [error, toast]);
  
  // Handle drag and drop
  const handleDragEnd = async (result) => {
    // If dropped outside a droppable area
    if (!result.destination) return;
    
    const sourceStageId = result.source.droppableId;
    const destinationStageId = result.destination.droppableId;
    
    // If dropped in the same stage at the same index, do nothing
    if (
      sourceStageId === destinationStageId &&
      result.source.index === result.destination.index
    ) {
      return;
    }
    
    // Get the lead that was dragged
    const draggedLeadId = result.draggableId;
    const allTableData = table.getRowModel().rows;
    
    // Find the lead in the table data
    let draggedLead = null;
    for (const row of allTableData) {
      if (row.original.leadId === draggedLeadId) {
        draggedLead = row.original;
        break;
      }
    }
    
    if (!draggedLead) {
      console.error('Dragged lead not found:', draggedLeadId);
      return;
    }
    
    // Optimistically update the stage (we'll revert if the API call fails)
    try {
      const leadToUpdate = { ...draggedLead };
      
      // If there's a stage object, update it, otherwise create a new stage object
      if (typeof leadToUpdate.stage === 'object') {
        leadToUpdate.stage = { ...leadToUpdate.stage, _id: destinationStageId };
      } else {
        leadToUpdate.stage = { _id: destinationStageId };
      }
      
      // Call the updateLead mutation
      await updateLead({
        leadId: draggedLead.leadId,
        updatedLead: { stage: destinationStageId }
      });
      
    } catch (error) {
      // If there's an error, show a toast
      toast({
        title: 'Error updating lead',
        description: 'There was an error moving the lead. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      
      // We don't need to manually revert the UI state since React Query
      // will invalidate the query and refetch the data
    }
  };
  
  // Filter leads by search term
  const handleSearch = (term) => {
    setSearchTerm(term);
    // Update filters with search term
    setFilters(prev => ({
      ...prev,
      search: term ? term : undefined
    }));
  };
  
  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
  };
  
  // Handle loading more leads
  const handleLoadMore = async () => {
    console.log('Manually loading more leads...');
    try {
      const result = await loadMoreLeads();
      console.log('Load more result:', {
        newLeadsCount: result.data?.pages?.[result.data.pages.length-1]?.leads?.length || 0,
        totalLeadsNow: leads.length,
        hasMorePages: result.hasNextPage
      });
    } catch (error) {
      console.error('Error loading more leads:', error);
    }
  };
  
  return (
    <Box p={4} h="100%">
      <TanFilters 
        searchTerm={searchTerm}
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
        currentFilters={filters}
      />
      
      {/* Loading indicator */}
      {isLoading && leads.length === 0 && (
        <Center py={10}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text ml={4} fontSize="lg">Loading leads...</Text>
        </Center>
      )}
      
      {/* Error message */}
      {error && leads.length === 0 && (
        <Center py={10}>
          <Text color="red.500">Error loading leads. Please try again.</Text>
        </Center>
      )}
      
      {/* Empty state */}
      {!isLoading && !error && leads.length === 0 && (
        <Center py={10}>
          <Text fontSize="lg">No leads found matching your filters.</Text>
        </Center>
      )}
      
      {/* Main board */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Flex 
          mt={4} 
          overflowX="auto" 
          h="calc(100vh - 200px)" 
          pb={4}
          sx={{
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              borderRadius: '4px',
            },
          }}
        >
          {stages.map(stage => {
            // Log the stage for debugging
            console.log(`Processing stage in TanKanbanBoard:`, { stageName: stage.name, stageId: stage._id });

            // Get leads for this stage directly from the leads array, not from the table
            const stageLeads = leads.filter(lead => {
              // Get the stage ID from the lead (handle different formats)
              let leadStageId;
              
              if (lead.stage) {
                if (typeof lead.stage === 'object' && lead.stage._id) {
                  leadStageId = lead.stage._id;
                } else {
                  leadStageId = String(lead.stage); // Convert to string for consistent comparison
                }
              } else if (lead.stageId) {
                leadStageId = lead.stageId;
              }
              
              // Direct comparison with stage._id (both as strings for consistency)
              return String(leadStageId) === String(stage._id);
            });
            
            console.log(`Found ${stageLeads.length} leads for stage ${stage.name} (${stage._id})`, {
              stageId: stage._id,
              totalLeadsCount: leads.length
            });
            
            return (
              <TanColumn 
                key={stage._id} 
                stage={stage} 
                leads={stageLeads}
                dragDisabled={isUpdatingLead}
                isCollapsed={collapsedColumns[stage._id] || false}
                onToggleCollapse={() => {
                  setCollapsedColumns(prev => ({
                    ...prev,
                    [stage._id]: !prev[stage._id]
                  }));
                }}
              />
            );
          })}
        </Flex>
      </DragDropContext>
      
      {/* Load more button and lead count */}
      <Flex justify="center" direction="column" align="center" mt={4}>
        <Text mb={2} fontSize="sm">
          {leads.length} leads loaded {hasMore ? '(more available)' : '(all loaded)'}
        </Text>
        
        <Button 
          onClick={handleLoadMore} 
          isLoading={isFetchingNextPage} 
          colorScheme="blue"
          loadingText="Loading more leads..."
          isDisabled={!hasMore || isLoadingAllLeads}
          mb={2}
        >
          {hasMore ? 'Load More Leads' : 'All Leads Loaded'}
        </Button>
        
        {isLoadingAllLeads && (
          <Text fontSize="sm" color="blue.500" fontStyle="italic">
            Auto-loading all pages in progress...
          </Text>
        )}
      </Flex>
      
      {/* Loading all indicator */}
      {isLoadingAllLeads && (
        <Flex justify="center" mt={4}>
          <Spinner size="sm" color="blue.500" mr={2} />
          <Text fontSize="sm" color="gray.600">
            Loading all leads in the background...
          </Text>
        </Flex>
      )}
    </Box>
  );
};

export default TanKanbanBoard;
